<template>
  <layout-admin current="chat-threads">
    <div class="threads">
      <aside class="threads__list">
        <header class="threads__list-head">
          <h1>Threads</h1>
          <span>{{ threads.length }}</span>
        </header>

        <div class="threads__list-body">
          <Link
            v-for="thread in threads"
            :key="thread.id"
            :href="route('admin.chat.threads', thread.id)"
            class="threads__item"
            :class="{ 'is-active': selected && selected.id === thread.id }"
          >
            <div class="threads__item-title">{{ thread.lead_name || ('Conversation #' + thread.id) }}</div>
            <div class="threads__item-meta">
              <span v-if="thread.lead_status">{{ thread.lead_status }}</span>
              <span v-if="thread.handoffs_count">{{ thread.handoffs_count }} handoff{{ thread.handoffs_count === 1 ? '' : 's' }}</span>
              <span v-if="!thread.lead_status && !thread.handoffs_count">No lead</span>
              <span v-if="thread.last_activity_at">{{ formatShort(thread.last_activity_at) }}</span>
            </div>
          </Link>

          <div v-if="threads.length === 0" class="threads__empty">
            No conversations yet.
          </div>
        </div>
      </aside>

      <section class="threads__chat">
        <template v-if="selected">
          <header class="threads__chat-head">
            <div>
              <h2>{{ selected.lead && selected.lead.name ? selected.lead.name : ('Conversation #' + selected.id) }}</h2>
              <p>
                #{{ selected.id }}
                <template v-if="selected.lead">
                  <span class="threads__status">{{ selected.lead.status }}</span>
                </template>
                <template v-if="selected.last_activity_at"> · {{ formatShort(selected.last_activity_at) }}</template>
                <template v-if="selected.ip_address"> · {{ selected.ip_address }}</template>
              </p>
            </div>
          </header>

          <div v-if="selected.lead" class="threads__lead-summary">
            <p v-if="selected.lead.summary" class="threads__lead-summary-text">
              {{ selected.lead.summary }}
            </p>
            <p v-else class="threads__lead-summary-text is-muted">
              No lead summary yet.
            </p>
            <div class="threads__lead-facts">
              <span v-if="selected.lead.phone">{{ selected.lead.phone }}</span>
              <span v-if="selected.lead.email">{{ selected.lead.email }}</span>
              <span v-if="selected.lead.preferred_language">Lang: {{ selected.lead.preferred_language }}</span>
              <span v-if="selected.lead.satisfaction_score != null">Score: {{ selected.lead.satisfaction_score }}</span>
              <span v-if="nextAppointmentLabel">Next appt: {{ nextAppointmentLabel }}</span>
              <span v-if="selected.lead.sales_ready_reason">Ready: {{ selected.lead.sales_ready_reason }}</span>
            </div>
            <p v-if="selected.lead.requirements" class="threads__lead-note">
              <strong>Requirements:</strong> {{ selected.lead.requirements }}
            </p>
            <p v-if="selected.lead.preferences" class="threads__lead-note">
              <strong>Preferences:</strong> {{ selected.lead.preferences }}
            </p>
          </div>

          <div v-if="handoffs.length" class="threads__handoffs">
            <article v-for="handoff in handoffs" :key="handoff.id" class="threads__handoff">
              <div class="threads__handoff-meta">
                <strong>{{ preferredLabel(handoff.preferred) }}</strong>
                <time>{{ formatShort(handoff.created_at) }}</time>
              </div>
              <p>{{ handoff.reason }}</p>
              <div class="threads__handoff-contact">
                <span v-if="handoff.phone">{{ handoff.phone }}</span>
                <span v-if="handoff.email">{{ handoff.email }}</span>
                <span v-if="handoff.referral">Referral: {{ handoff.referral }}</span>
                <span v-if="!handoff.phone && !handoff.email">No contact left</span>
                <span v-if="!handoff.notified">Telegram did not send</span>
              </div>
            </article>
          </div>

          <div ref="messagesEl" class="threads__messages">
            <div
              v-for="message in messages"
              :key="message.id"
              class="turn"
              :class="message.role === 'assistant' ? 'is-out' : 'is-in'"
            >
              <details v-if="message.reasoning" class="bubble__reasoning">
                <summary>Thought</summary>
                <div class="prose-inline" v-html="bodyHtml(message.reasoning)"></div>
              </details>
              <div
                v-for="(card, cardIndex) in (message.cards || [])"
                :key="cardIndex"
                class="bubble"
              >
                <div class="bubble__body">
                  <figure class="bubble__card">
                    <img v-if="card.image" :src="card.image" alt="">
                    <div v-if="card.caption" class="prose-inline" v-html="bodyHtml(card.caption)"></div>
                  </figure>
                </div>
              </div>
              <div v-if="message.content" class="bubble">
                <div class="bubble__body">
                  <div class="bubble__text prose-inline" v-html="bodyHtml(message.content)"></div>
                </div>
                <div class="bubble__meta">
                  <time>{{ formatShort(message.created_at) }}</time>
                </div>
              </div>
              <div v-else-if="message.cards && message.cards.length" class="bubble__meta">
                <time>{{ formatShort(message.created_at) }}</time>
              </div>
            </div>

            <div v-if="messages.length === 0" class="threads__empty">
              This conversation has no messages.
            </div>
          </div>
        </template>

        <div v-else class="threads__placeholder">
          Select a conversation to read it.
        </div>
      </section>
    </div>
  </layout-admin>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import LayoutAdmin from '@/Layouts/Admin.vue';
import { renderMarkdown } from '@/markdown';

export default {
  components: {
    LayoutAdmin,
    Link
  },

  props: {
    threads: { type: Array, default: () => [] },
    selected: { type: Object, default: null },
    messages: { type: Array, default: () => [] },
    handoffs: { type: Array, default: () => [] }
  },

  computed: {
    nextAppointmentLabel() {
      const lead = this.selected && this.selected.lead;
      const appointments = lead && Array.isArray(lead.appointments) ? lead.appointments : [];
      const next = appointments.find((item) => item && item.status === 'scheduled') || appointments[0];

      if (!next) {
        return '';
      }

      const when = next.starts_at ? this.formatShort(next.starts_at) : (next.preferred_time_text || '');

      if (!when) {
        return next.status || '';
      }

      return when + (next.status ? (' (' + next.status + ')') : '');
    }
  },

  watch: {
    messages: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          const el = this.$refs.messagesEl;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    }
  },

  methods: {
    formatShort(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    preferredLabel(preferred) {
      if (preferred === 'whatsapp') return 'WhatsApp';
      if (preferred === 'email') return 'Email';
      return 'No contact';
    },

    bodyHtml(text) {
      return renderMarkdown(String(text || ''));
    }
  }
};
</script>

<style lang="scss" scoped>
.threads {
  --border: var(--admin-border);
  --muted: var(--admin-muted);
  --surface: var(--admin-surface);

  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--admin-radius);
  display: grid;
  flex: 1;
  grid-template-columns: minmax(240px, 320px) 1fr;
  min-height: 0;
  overflow: hidden;
}

.threads__list {
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.threads__list-head {
  align-items: baseline;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  padding: 0.9rem 1rem;

  h1 {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0;
  }

  span {
    color: var(--muted);
    font-size: 0.75rem;
  }
}

.threads__list-body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.threads__item {
  border-bottom: 1px solid #f4f4f5;
  color: inherit;
  display: block;
  padding: 0.65rem 0.85rem;
  text-decoration: none;
  transition: background 0.12s ease;

  &:hover {
    background: #fafafa;
  }

  &.is-active {
    background: #f4f4f5;
  }
}

.threads__item-title {
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 0.2rem;
}

.threads__item-meta {
  color: var(--muted);
  display: flex;
  font-size: 0.72rem;
  gap: 0.5rem;
  justify-content: space-between;
}

.threads__chat {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 0;
}

.threads__status {
  background: #f4f4f5;
  border-radius: 999px;
  margin-left: 0.35rem;
  padding: 0.1rem 0.45rem;
}

.threads__lead-summary {
  background: #fafafa;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  padding: 0.75rem 1.1rem 0.85rem;
}

.threads__lead-summary-text {
  font-size: 0.84rem;
  line-height: 1.45;
  margin: 0 0 0.55rem;
  white-space: pre-wrap;

  &.is-muted {
    color: var(--muted);
  }
}

.threads__lead-facts {
  color: var(--muted);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.72rem;
  gap: 0.35rem 0.75rem;

  span:empty {
    display: none;
  }
}

.threads__lead-note {
  font-size: 0.78rem;
  line-height: 1.45;
  margin: 0.45rem 0 0;

  strong {
    font-weight: 600;
  }
}

.threads__chat-head {
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  padding: 0.9rem 1.1rem;

  h2 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.25rem;
  }

  p {
    color: var(--muted);
    font-size: 0.78rem;
    margin: 0;
  }
}

.threads__handoffs {
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 0.65rem;
  max-height: 14rem;
  overflow-y: auto;
  padding: 0.75rem 1.1rem;
}

.threads__handoff {
  background: #fafafa;
  border: 1px solid var(--border);
  border-radius: calc(var(--admin-radius) - 2px);
  padding: 0.6rem 0.75rem;

  p {
    font-size: 0.84rem;
    line-height: 1.45;
    margin: 0.3rem 0;
  }
}

.threads__handoff-meta,
.threads__handoff-contact {
  color: var(--muted);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.72rem;
  gap: 0.35rem 0.75rem;
}

.threads__handoff-meta strong {
  color: inherit;
  font-weight: 600;
}

.threads__messages {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1.1rem;
}

.threads__placeholder,
.threads__empty {
  color: var(--muted);
  font-size: 0.9rem;
  margin: auto;
  padding: 2rem;
  text-align: center;
}

.turn {
  display: contents;

  &.is-in .bubble,
  &.is-in .bubble__reasoning,
  &.is-in .bubble__meta {
    align-self: flex-start;
  }

  &.is-out .bubble,
  &.is-out .bubble__reasoning,
  &.is-out .bubble__meta {
    align-self: flex-end;
  }

  &.is-out .bubble__body {
    background: #18181b;
    color: #fafafa;
  }

  &.is-out .bubble__meta {
    justify-content: flex-end;
  }
}

.bubble {
  display: flex;
  flex-direction: column;
  max-width: min(72%, 560px);
}

.bubble__reasoning {
  color: var(--muted);
  font-size: 0.75rem;
  margin-bottom: 0.35rem;

  summary {
    cursor: pointer;
  }

  p {
    margin: 0.35rem 0 0;
    white-space: pre-wrap;
  }
}

.bubble__body {
  background: #f4f4f5;
  border-radius: 1rem;
  padding: 0.7rem 0.85rem;
}

.bubble__card {
  margin: 0;

  img {
    border-radius: 0.6rem;
    display: block;
    height: auto;
    margin-bottom: 0.45rem;
    max-width: 100%;
    object-fit: contain;
    width: 100%;
  }
}

.bubble__text {
  font-size: 0.88rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.bubble__meta {
  color: var(--muted);
  display: flex;
  font-size: 0.7rem;
  margin-top: 0.25rem;
}
</style>
